import { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Checkbox, EmptyState, Field, Input, PageHeader, Spinner, Textarea } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import {
  createInsurerGrant,
  getInsurabilityPack,
  insurabilityPackPdfUrl,
  issueCdsToken,
  listInsurabilityPacks,
  previewInsurabilityPack,
  type CdsGrantRow,
  type InsurabilityPackDocument,
  type InsurabilityPackSummary,
  type InsurerKind,
  type PackLink,
  type PackRisk,
} from '../lib/api';
import { fmtDate } from '../lib/format';
import { ActionDialog, errorText, useAnnounce } from '../components/today/shared';
import { LegalSection } from '../components/insurability/LegalSection';
import { CreateLinkDialog, INSURER_KINDS, InsurerAccessCard, WithdrawDialog, cdsGrantsKey, packLinksKey, type LinkDraft } from '../components/insurability/PackSharing';
import { PackSignDialog } from '../components/insurability/PackSignDialog';

/**
 * /w/:slug/app/insurability — V111-5-5 (ADR-0094); v11.6 V116-4-4/4-5 (ADR-0114, build review v116-track4).
 * Executive-facing: one verdict line, the gaps to close (each a link to where it's fixed), the legal section
 * (format /3), the risks, the signed versions (with Withdraw) and one "Insurer access" list (keyed links + API data
 * access). Actions: sign a version (the one primary) and share it with an insurer or broker (a keyed link; API
 * data access is the secondary option inside that flow).
 */

const card: React.CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', background: 'var(--surface)',
  padding: 'var(--space-4)', display: 'grid', gap: 'var(--space-3)',
};
const h2: React.CSSProperties = { margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' };

/** What a reader of a signed version would see differently from the draft (the parts a sign changes). */
export function packFingerprint(doc: InsurabilityPackDocument | undefined): string {
  if (!doc) return '';
  return JSON.stringify({
    counts: doc.cover.counts,
    risks: doc.risks.map((r) => [r.register_item_id, r.controls.map((c) => [c.control_id, c.state])]),
    laws: (doc.legal?.laws ?? []).map((l) => [l.law_id, l.counted, l.controls.passing, l.controls.of]),
    rows: (doc.legal?.rows ?? []).map((r) => [r.control_id, r.state, r.counted, r.gap?.fix_by ?? null]),
  });
}

export function InsurabilityPack() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug;
  const loc = useLocation();
  const qc = useQueryClient();
  const preview = useQuery({ queryKey: ['insurability-preview', slug], queryFn: () => previewInsurabilityPack(slug) });
  const packs = useQuery({ queryKey: ['insurability-packs', slug], queryFn: () => listInsurabilityPacks(slug) });
  const canSign = role === 'owner' || role === 'admin';
  // `back`: where signing was started from. "Sign first" in the share dialog returns to it (V116-4-5 follow-up).
  const [signOpen, setSignOpen] = useState<null | { privilege: boolean; back?: 'link' }>(null);
  const [linking, setLinking] = useState(false);
  // The share form while the user signs first, and the line the share dialog shows when it comes back signed.
  const linkDraft = useRef<LinkDraft | null>(null);
  const keepDraft = useCallback((d: LinkDraft) => { linkDraft.current = d; }, []);
  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [live, announce] = useAnnounce();
  const latest = packs.data?.packages[0] ?? null;
  const latestDetail = useQuery({
    queryKey: ['insurability-pack', slug, latest?.package_id],
    queryFn: () => getInsurabilityPack(slug, latest!.package_id),
    enabled: !!latest,
  });

  if (preview.isLoading) return <Spinner label="Loading insurability pack" />;
  if (preview.error || !preview.data) {
    return <Alert variant="error">{preview.error?.message ?? 'Could not build the pack.'}</Alert>;
  }
  const doc = preview.data.document;
  const gaps = doc.cover.counts.uncovered_exposures;
  const systemNames = new Map(doc.systems.map((s) => [s.system_id, s.name]));
  const nextVersion = (latest?.version ?? 0) + 1;
  const draftBehind = !!latest && !!latestDetail.data && packFingerprint(latestDetail.data.package.content) !== packFingerprint(doc);
  const backPath = `${loc.pathname}${loc.search}`;
  // FLOW-T4-14: the readers are everyone who can read the next version (links and API grants).
  const linkNames = (qc.getQueryData<{ links: PackLink[] }>(packLinksKey(slug))?.links ?? []).filter((l) => l.status === 'active').map((l) => l.recipient_name);
  const grantNames = (qc.getQueryData<{ grants: CdsGrantRow[] }>(cdsGrantsKey(slug))?.grants ?? [])
    .filter((g) => g.status === 'active' && g.package_kinds.some((k) => INSURER_KINDS.has(k))).map((g) => g.agency_name);
  const readers = [...new Set([...linkNames, ...grantNames])];
  const lawRuleIds = new Set((doc.legal?.laws ?? []).flatMap((l) => l.rule_ids));
  const openSign = (privilege = false, back?: 'link') => { setLinking(false); setSignOpen({ privilege, back }); };
  const focusLater = (id: string) => setTimeout(() => document.getElementById(id)?.focus(), 0);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '72rem' }}>
      {live}
      <PageHeader
        title="Insurability pack"
        right={canSign ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {latest ? <Button id="ip-share-btn" variant="secondary" compact onClick={() => setLinking(true)}>Share with an insurer</Button> : null}
            <Button id="ip-sign-btn" variant="primary" compact onClick={() => openSign()}>
              {latest ? `Sign version ${nextVersion}` : 'Sign the pack'}
            </Button>
          </div>
        ) : null}
      />
      <Verdict gaps={gaps} latest={latest} canSign={canSign} draftBehind={draftBehind} />

      <Figures doc={doc} />
      {doc.cover.outside_sources_note ? <Alert variant="info">{doc.cover.outside_sources_note}</Alert> : null}

      <UncoveredCard doc={doc} slug={slug} />
      {doc.legal ? (
        <div style={card}>
          <LegalSection
            legal={doc.legal}
            changes={doc.changes ?? null}
            audience="internal"
            systemNames={systemNames}
            slug={slug}
            backPath={backPath}
            onIncludePrivileged={canSign ? () => openSign(true) : null}
          />
        </div>
      ) : null}
      <RisksCard risks={doc.legal ? doc.risks.filter((r) => !r.tracking) : doc.risks} slug={slug} systemNames={systemNames} />
      <VersionsCard slug={slug} packs={packs.data?.packages ?? []} canWithdraw={canSign} announce={announce} />
      <InsurerAccessCard
        slug={slug}
        canManage={canSign}
        hasSigned={!!latest}
        onCreateLink={() => setLinking(true)}
        onShareApi={() => setSharing(true)}
        onSignFirst={canSign ? () => openSign(false, 'link') : null}
        announce={announce}
      />

      {linking && latest ? (
        <CreateLinkDialog
          slug={slug}
          latest={{ package_id: latest.package_id, version: latest.version }}
          draftVersion={nextVersion}
          draftBehind={draftBehind}
          onSignFirst={() => openSign(false, 'link')}
          onShareApi={() => { setLinking(false); setSharing(true); }}
          onClose={() => { setLinking(false); linkDraft.current = null; setLinkNotice(null); focusLater('ip-access-h'); }}
          onCreated={(name) => { linkDraft.current = null; void qc.invalidateQueries({ queryKey: packLinksKey(slug) }); announce(`Link created for ${name}.`); }}
          draft={linkDraft.current}
          onDraft={keepDraft}
          notice={linkNotice}
        />
      ) : null}
      <PackSignDialog
        open={signOpen !== null}
        slug={slug}
        nextVersion={nextVersion}
        gaps={gaps}
        withheld={doc.legal?.withheld_privileged ?? 0}
        lawRuleIds={lawRuleIds}
        systemNames={systemNames}
        defaultRecipients={readers}
        startWithPrivilege={signOpen?.privilege ?? false}
        initialCarried={preview.data.carried ?? null}
        backPath={backPath}
        cancelLabel={signOpen?.back === 'link' ? 'Back to sharing' : undefined}
        onClose={() => { if (signOpen?.back === 'link') setLinking(true); setSignOpen(null); }}
        onSigned={(p) => {
          const back = signOpen?.back;
          setSignOpen(null);
          const fresh = Promise.all([
            qc.refetchQueries({ queryKey: ['insurability-packs', slug] }),
            qc.refetchQueries({ queryKey: ['insurability-preview', slug] }),
          ]);
          if (back !== 'link') { announce(`Version ${p.version} signed.`); return; }
          // The share dialog comes back on the version just signed, never on the one before it.
          void fresh.finally(() => { setLinkNotice(`Version ${p.version} signed. The link opens it.`); setLinking(true); });
        }}
      />
      {sharing ? <ShareDialog slug={slug} onClose={() => { setSharing(false); focusLater('ip-access-h'); }} onShared={(name) => { void qc.invalidateQueries({ queryKey: cdsGrantsKey(slug) }); announce(`API data access created for ${name}.`); }} /> : null}
    </div>
  );
}

/** The 5-second answer: ready or not, and what to do. Glyph + words, never color alone. */
function Verdict({ gaps, latest, canSign, draftBehind }: {
  gaps: number; latest: InsurabilityPackSummary | null; canSign: boolean; draftBehind: boolean;
}) {
  // FLOW-T4-08: a withdrawn latest version is the headline fact: readers with a link see "Do not rely".
  const withdrawn = !!latest?.withdrawn_at;
  const signedLine = !latest ? 'Not signed yet.'
    : withdrawn ? `Version ${latest.version} withdrawn ${fmtDate(latest.withdrawn_at)} · readers with a link see “Do not rely”.`
    : draftBehind ? `Version ${latest.version} signed ${fmtDate(latest.signed_at)} · changes since then aren’t signed.`
    : `Version ${latest.version} is current (signed ${fmtDate(latest.signed_at)}).`;
  const warn = gaps > 0 || withdrawn;
  return (
    <div
      data-testid="ip-verdict"
      style={{
        display: 'flex', gap: 'var(--space-3)', alignItems: 'baseline', flexWrap: 'wrap',
        padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-card)',
        background: warn ? 'var(--warning-bg)' : 'var(--success-bg)',
        color: warn ? 'var(--warning-fg)' : 'var(--success-fg)',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>
        <span aria-hidden="true">{warn ? '⚠ ' : '✓ '}</span>
        {gaps > 0 ? `${gaps} exposure${gaps === 1 ? '' : 's'} uncovered` : withdrawn ? 'Sign a replacement' : 'Ready to sign'}
      </span>
      <span style={{ fontSize: 'var(--font-size-2xs)' }}>
        {gaps > 0 ? <a href="#ip-uncovered" style={{ color: 'inherit', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', minHeight: 'var(--control-compact-min-h)' }}>Close them before you sign</a> : null}
        {gaps > 0 ? ' · ' : ''}{signedLine}{!canSign && !latest ? ' An owner or admin signs it.' : ''}
      </span>
    </div>
  );
}
function Figures({ doc }: { doc: InsurabilityPackDocument }) {
  const c = doc.cover.counts;
  const covered = c.risks - doc.uncovered_exposures.risks.length;
  const items: Array<{ label: string; value: string }> = [
    { label: 'Uncovered exposures', value: c.uncovered_exposures > 0 ? `⚠ ${c.uncovered_exposures}` : `✓ 0` },
    { label: 'Risks with a control', value: `${covered} of ${c.risks}` },
    { label: 'AI systems', value: String(c.systems) },
    { label: 'Incidents on record', value: String(doc.loss_ledger.incident_count) },
  ];
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(11rem, 1fr))' }}>
      {items.map((i) => (
        <li key={i.label} style={{ ...card, gap: '2px' }}>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>{i.label}</span>
          <span style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{i.value}</span>
        </li>
      ))}
    </ul>
  );
}

const REASON: Record<string, string> = {
  no_mitigating_controls: 'No control mitigates it',
  applicability_unresolved: 'Applicability not confirmed',
};

const dutyLabel = (duty: string) => {
  const s = duty.replace(/^duty:/, '').replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

function UncoveredCard({ doc, slug }: { doc: InsurabilityPackDocument; slug: string }) {
  const risks = doc.uncovered_exposures.risks;
  const duties = doc.uncovered_exposures.unmapped_obligations;
  // The register page is keyed by the risk item, not the workspace register row.
  const riskIdOf = new Map(doc.risks.map((x) => [x.register_item_id, x.risk_item_id]));
  return (
    <section id="ip-uncovered" tabIndex={-1} style={card} aria-labelledby="ip-uncovered-h">
      <h2 id="ip-uncovered-h" style={h2}>Uncovered exposures</h2>
      {risks.length + duties.length === 0 ? (
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
          <span aria-hidden="true">✓ </span>Every applicable risk has at least one mitigating control.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}>
          {risks.map((r) => (
            <li key={r.register_item_id} style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}>
              <Link to={`/w/${slug}/app/register/${encodeURIComponent(riskIdOf.get(r.register_item_id) ?? r.register_item_id)}`} style={{ fontWeight: 600 }}>{r.title}</Link>
              <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>{REASON[r.reason] ?? 'Needs review'}</span>
            </li>
          ))}
          {duties.map((d) => (
            <li key={`${d.system_id}-${d.duty}`} style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'baseline' }}>
              <Link
                to={d.system_id ? `/w/${slug}/app/systems/${encodeURIComponent(d.system_id)}?tab=requirements` : `/w/${slug}/app/systems`}
                style={{ fontWeight: 600 }}
              >
                {dutyLabel(d.duty)}
              </Link>
              <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>A legal duty with no control mapped</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const money = (cents: number) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0, notation: cents >= 100_000_000 ? 'compact' : 'standard' }).format(cents / 100);

function RisksCard({ risks, slug, systemNames }: { risks: PackRisk[]; slug: string; systemNames: Map<string, string> }) {
  if (risks.length === 0) {
    return (
      <EmptyState
        title="No applicable risks yet"
        description="The pack lists the recognized risks that apply to your AI systems."
        action={<Link to={`/w/${slug}/app/register`}>Open the register</Link>}
      />
    );
  }
  return (
    <section style={card} aria-labelledby="ip-risks">
      <h2 id="ip-risks" style={h2}>Risks in the pack</h2>
      <div style={{ overflowX: 'auto' }} tabIndex={0} role="region" aria-labelledby="ip-risks">
        <table style={{ width: '100%', minWidth: '36rem', borderCollapse: 'collapse', fontSize: 'var(--font-size-2xs)' }}>
          <thead>
            <tr>
              {['Risk', 'Controls passing', 'Bad year (1 in 20)', 'Losses on record'].map((h) => (
                <th key={h} scope="col" style={{ textAlign: 'left', padding: 'var(--space-2)', color: 'var(--text-dim)', fontWeight: 500, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {risks.map((r) => {
              const passing = r.controls.filter((c) => c.state === 'pass').length;
              const href = `/w/${slug}/app/register/${encodeURIComponent(r.risk_item_id)}`;
              return (
                <tr key={r.register_item_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <th scope="row" style={{ textAlign: 'left', padding: 'var(--space-2)', fontWeight: 500 }}>
                    <Link to={href}>{r.title}</Link>
                    {r.system_ids && r.system_ids.length > 0 ? (
                      <span style={{ display: 'block', fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)', fontWeight: 400 }}>
                        {r.system_ids.map((id) => systemNames.get(id) ?? id).join(' · ')}
                      </span>
                    ) : null}
                    {r.outside_selected_sources ? <span style={{ display: 'block', fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)', fontWeight: 400 }}>Outside your selected rule sources</span> : null}
                  </th>
                  <td style={{ padding: 'var(--space-2)', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                    {r.controls.length === 0 ? <span style={{ color: 'var(--text-dim)' }}>None mapped</span> : `${passing} of ${r.controls.length}`}
                  </td>
                  <td style={{ padding: 'var(--space-2)', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                    {r.residual?.run ? money(r.residual.run.p95 * 100) : <span style={{ color: 'var(--text-dim)' }}>Not estimated</span>}
                  </td>
                  <td style={{ padding: 'var(--space-2)', color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                    {r.loss_history ? `${money(r.loss_history.loss_amount_cents)} · ${r.loss_history.incident_count} incident${r.loss_history.incident_count === 1 ? '' : 's'}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function VersionsCard({ slug, packs, canWithdraw, announce }: {
  slug: string; packs: InsurabilityPackSummary[]; canWithdraw: boolean; announce: (m: string) => void;
}) {
  const qc = useQueryClient();
  const [target, setTarget] = useState<{ package_id: string; version: number } | null>(null);
  if (packs.length === 0) return null;
  return (
    <section style={card} aria-labelledby="ip-versions">
      <h2 id="ip-versions" tabIndex={-1} style={h2}>Signed versions</h2>
      <ul className="ip-rows">
        {packs.map((p) => (
          <li key={p.package_id} className="ip-row">
            <span style={{ color: 'var(--text)' }}>Version {p.version} · {fmtDate(p.signed_at)}</span>
            {p.withdrawn_at ? <Badge>Withdrawn {fmtDate(p.withdrawn_at)}</Badge>
              : p.superseded_by_package_id ? <span className="ip-dim">Replaced</span>
              : <Badge tone="accent">Current</Badge>}
            {p.withdrawn_at && p.withdrawal_reason ? <span className="ip-dim">{p.withdrawal_reason}</span> : null}
            <span className="ip-acts">
              <a className="ip-law-link" href={insurabilityPackPdfUrl(slug, p.package_id)} download>
                Download PDF<span className="sr-only">, version {p.version}</span>
              </a>
              <a className="ip-law-link" href={`/verify/insurability-pack/${encodeURIComponent(p.package_id)}`} target="_blank" rel="noopener noreferrer">
                Check signature<span className="sr-only"> of version {p.version}</span><span aria-hidden="true"> ↗</span><span className="sr-only"> (opens in a new tab)</span>
              </a>
              {canWithdraw && !p.withdrawn_at ? (
                <button type="button" className="ip-danger-link" onClick={() => setTarget({ package_id: p.package_id, version: p.version })}>
                  Withdraw<span className="sr-only"> version {p.version}</span>
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      <WithdrawDialog
        slug={slug}
        pack={target}
        onClose={() => setTarget(null)}
        onDone={(m) => {
          void qc.invalidateQueries({ queryKey: ['insurability-pack', slug] });
          void qc.invalidateQueries({ queryKey: ['insurability-packs', slug] });
          setTarget(null);
          announce(m);
          // A11Y-T4-03: focus lands on a stable element, the card heading.
          setTimeout(() => document.getElementById('ip-versions')?.focus(), 0);
        }}
      />
    </section>
  );
}

const KINDS: Array<{ value: InsurerKind; label: string }> = [
  { value: 'ai_inventory', label: 'AI systems inventory' },
  { value: 'ai_disclosure', label: 'Risks and exposures' },
  { value: 'loss_history', label: 'Loss history' },
];

/**
 * API data access (CDS grant + token) for an insurer's systems team. The secondary sharing path: the message carries
 * the API endpoint and key only, never a bare verify URL (which shows no contents; FLOW-T4-01).
 */
function ShareDialog({ slug, onClose, onShared }: { slug: string; onClose: () => void; onShared: (name: string) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kinds, setKinds] = useState<InsurerKind[]>(['ai_inventory', 'ai_disclosure', 'loss_history']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const apiBase = typeof window !== 'undefined' ? `${window.location.origin}/api/cds/v1/views` : '/api/cds/v1/views';

  async function create(): Promise<boolean> {
    if (token) {
      if (!saved) { setError('Copy the key, or confirm you saved it, before closing. It can’t be shown again.'); return false; }
      onClose();
      return true;
    }
    if (name.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email) || kinds.length === 0) {
      setError('Add the insurer’s name, a contact email and at least one section.');
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const expires = new Date(Date.now() + 90 * 86_400_000).toISOString();
      const { grant } = await createInsurerGrant(slug, { agency_name: name.trim(), agency_contact_email: email.trim(), package_kinds: kinds, expires_at: expires });
      const issued = await issueCdsToken(slug, grant.grant_id);
      setToken(issued.token);
      setExpiresAt(expires);
      onShared(name.trim());
      setTimeout(() => document.getElementById('share-copy')?.focus(), 0);
      return false;
    } catch (err) {
      setError(errorText(err, 'Could not share the pack.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(message(token));
      setCopied(true);
      setSaved(true);
      setError(null);
    } catch {
      setError('Copy failed. Select the key and copy it manually, then tick the box below.');
    }
  }

  function message(key: string): string {
    return [
      'We have given your systems team read access to our signed AI insurability pack, as data.',
      expiresAt ? `Access runs until ${fmtDate(expiresAt)}.` : '',
      '',
      'Read the full pack as JSON:',
      `  ${apiBase}`,
      `  Authorization: Bearer ${key}`,
    ].filter((l, i) => l !== '' || i === 2).join('\n'); // keep the one blank separator line
  }

  function close() {
    if (token && !saved) {
      setError('This key is shown once. Copy it, or tick “I’ve saved the key”, before closing.');
      return;
    }
    onClose();
  }

  return (
    <ActionDialog open title="Give API data access" submitLabel={token ? 'Done' : 'Create access'} onClose={close} onSubmit={create} busy={busy} error={error}
      cancelLabel={token ? 'Close' : 'Cancel'} submitDisabled={!!token && !saved} describedBy={token && !saved ? 'share-done-hint' : undefined}
      subtitle={token ? undefined : 'For an insurer’s systems team: read-only access to your latest signed version as data, for 90 days. Revoke it any time on this page.'}>
      {token ? (
        <div className="ip-stack">
          <Alert variant="success">Access created for {name}. Copy the message and send it to them from your own email. The key is shown once.</Alert>
          <Field id="share-message" label="Message to send">
            <Textarea id="share-message" readOnly rows={7} value={message(token)} onFocus={(e) => e.currentTarget.select()} />
          </Field>
          <div className="ip-acts">
            <Button id="share-copy" variant="primary" compact onClick={copy}>{copied ? 'Copied' : 'Copy message'}</Button>
            <Checkbox label="I’ve saved the key" checked={saved} onChange={(e) => { setSaved(e.target.checked); if (e.target.checked) setError(null); }} />
          </div>
          {!saved ? <p id="share-done-hint" className="ip-dim ip-flat">Copy the message or tick the box to finish.</p> : null}
          <p className="sr-only" role="status" aria-live="polite">{copied ? 'Message copied to the clipboard.' : ''}</p>
        </div>
      ) : (
        <>
          <Field id="share-name" label="Insurer or broker">
            <Input id="share-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field id="share-email" label="Contact email">
            <Input id="share-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <fieldset className="ip-fieldset">
            <legend className="ip-legend">Sections they can read</legend>
            {KINDS.map((k) => (
              <Checkbox key={k.value} label={k.label} checked={kinds.includes(k.value)}
                onChange={(e) => setKinds(e.target.checked ? [...kinds, k.value] : kinds.filter((x) => x !== k.value))} />
            ))}
          </fieldset>
        </>
      )}
    </ActionDialog>
  );
}
